import { useId, useMemo } from "react";
import ReactQuill from "react-quill";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Eye,
  Eraser,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Quote,
  Underline,
} from "lucide-react";
import { richTextToEditorValue, richTextToHtml } from "../../utils/richText";
import "react-quill/dist/quill.snow.css";
import "../../styles/rich-text-editor.css";

const FORMATS = ["header", "bold", "italic", "underline", "blockquote", "list", "bullet", "align"];

function ToolbarButton({ className, value, label, children }) {
  return (
    <button type="button" className={className} value={value} aria-label={label} title={label}>
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 220,
  previewPlaceholder = "A visualizacao aparecera aqui...",
  // Sem a prévia ao lado (o editor já mostra a formatação).
  preview = true,
}) {
  const toolbarId = `rich-editor-${useId().replace(/:/g, "")}`;

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
      },
      clipboard: {
        matchVisual: true,
      },
    }),
    [toolbarId]
  );

  const editorValue = useMemo(() => richTextToEditorValue(value || ""), [value]);
  const previewHtml = useMemo(() => richTextToHtml(value || ""), [value]);

  return (
    <div className="rich-editor">
      <div id={toolbarId} className="rich-editor-toolbar">
        <span className="rich-editor-group">
          <ToolbarButton className="ql-header" value="1" label="Titulo 1">
            <Heading1 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-header" value="2" label="Titulo 2">
            <Heading2 className="h-4 w-4" />
          </ToolbarButton>
        </span>

        <span className="rich-editor-group">
          <ToolbarButton className="ql-bold" label="Negrito">
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-italic" label="Italico">
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-underline" label="Sublinhado">
            <Underline className="h-4 w-4" />
          </ToolbarButton>
        </span>

        <span className="rich-editor-group">
          <ToolbarButton className="ql-list" value="bullet" label="Lista com bullets">
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-list" value="ordered" label="Lista numerada">
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-blockquote" label="Citacao">
            <Quote className="h-4 w-4" />
          </ToolbarButton>
        </span>

        <span className="rich-editor-group">
          <ToolbarButton className="ql-align" label="Alinhar a esquerda">
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-align" value="center" label="Centralizar">
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton className="ql-align" value="right" label="Alinhar a direita">
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
        </span>

        <span className="rich-editor-group">
          <ToolbarButton className="ql-clean" label="Limpar formatação">
            <Eraser className="h-4 w-4" />
          </ToolbarButton>
        </span>
      </div>

      <div className={preview ? "rich-editor-grid" : "rich-editor-grid rich-editor-grid--single"}>
        <div className="rich-editor-panel">
          <div className="rich-editor-frame" style={{ "--editor-min-height": `${minHeight}px` }}>
            <ReactQuill
              theme="snow"
              value={editorValue}
              // Só o que a pessoa escreve: ao abrir, o Quill normaliza o texto
              // (source "api") e isso não é uma alteração.
              onChange={(content, _delta, source) => {
                if (source === "user") onChange?.(content);
              }}
              modules={modules}
              formats={FORMATS}
              placeholder={placeholder}
            />
          </div>
          <p className="rich-editor-help">Pode colar texto do Word e ajustar a formatação na barra acima.</p>
        </div>

        {preview ? (
          <div className="rich-editor-preview">
            <div className="rich-editor-preview-title">
              <Eye className="h-4 w-4" />
              Previa
            </div>
            <div
              className="rich-editor-preview-body"
              dangerouslySetInnerHTML={{
                __html: previewHtml || `<p class="rich-editor-preview-empty">${previewPlaceholder}</p>`,
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
